import { DataTable } from "@/components/molecules/DataTable";
import { Checkbox } from "@/components/ui/checkbox";
import { CROExpandedCard } from "@/components/organisms/HeuristicAnalysisExpandedCard";
import { IdeaExpandedSheet } from "@/components/organisms/IdeaExpandedSheet";
import { PageExpandedSheet } from "@/components/organisms/PageExpandedSheet";
import { EntityCardGrid } from "@/components/organisms/EntityCardGrid";
import { ViewToggle } from "@/components/molecules/ViewToggle";
import { useAnalysis } from "@/hooks/useAnalysis";
import { useDomainIntegrations } from "@/hooks/useDomainIntegrations";
import { useDomains } from "@/hooks/useDomains";
import { useIdeas } from "@/hooks/useIdeas";
import { useJobs } from "@/hooks/useJobs";
import { useOrgRole } from "@/hooks/useOrgRole";
import { usePages } from "@/hooks/usePages";
import { useSonnerToast } from "@/hooks/useSonnerToast";
import { useUI } from "@/hooks/useUI";
import { useUser } from "@/hooks/useUser";
import { isDraftInProgress } from "@/lib/pageWorkflow";
import {
  WordPressIdeaConflictError,
  WordPressService,
} from "@/lib/wordpressService";
import {
  isWordPressConnected,
  type Idea,
  type Page,
  type WordPressVersion,
} from "@/schemas";
import type { HeuristicJob, Job } from "@/schemas/firestore/jobs.schema";
import { useStore } from "@/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { unifiedColumns } from "./columns/unifiedColumns";
import { auditToRow, ideaToRow, pageToRow } from "./adapters/toUnifiedRow";
import type { UnifiedRow, UnifiedRowKind } from "./adapters/toUnifiedRow";

const PAGE_EDIT_LOCK_TIMEOUT_MS = 30_000;
const ENTITY_TYPE_OPTIONS: ReadonlyArray<{
  value: UnifiedRowKind;
  label: string;
}> = [
  { value: "audit", label: "Analysis" },
  { value: "page", label: "Page" },
  { value: "idea", label: "Idea" },
];

export const RecommendationsSection: React.FC = () => {
  const {
    pendingJobs,
    completedJobsWithRecommendations,
    recentErrorJob,
    isLoadingJobs,
  } = useJobs();
  const { pages, isLoading: isLoadingPages } = usePages();
  const { currentDomain } = useDomains();
  const { currentUser } = useUser();
  // Audits, drafts, approvals and manual edits are all `min_role=editor` in
  // core; withholding the callbacks hides the affordances in both sheets.
  const { canEdit } = useOrgRole();
  const orgId = useStore((state) => state.orgId);
  const { startAnalysisJob, isAnalyzing, isGeneratingAnalysis } = useAnalysis();
  const {
    ideas,
    isLoadingIdeas,
    ideaUiOverrides,
    markIdeaCreatePending,
    resolveIdeaAsImplemented,
    clearIdeaUiOverride,
  } = useIdeas(currentDomain?.id);
  const { toast } = useSonnerToast();
  const { viewMode, setViewMode } = useUI();
  const wpIntegration = useStore((state) => state.wpIntegration);
  const wpVersions = useStore((state) => state.wpVersions);
  const wpCategories = useStore((state) => state.wpCategories);
  const isLoadingWpVersions = useStore((state) => state.isLoadingWpVersions);
  const wpVersionErrors = useStore((state) => state.wpVersionErrors);
  const wpActionLoading = useStore((state) => state.wpActionLoading);
  const editWpContent = useStore((state) => state.editWpContent);
  const saveManualPageVersion = useStore(
    (state) => state.saveManualPageVersion,
  );
  const approveWpVersion = useStore((state) => state.approveWpVersion);
  const subscribeToPageVersions = useStore(
    (state) => state.subscribeToPageVersions,
  );
  const pageIntegrations = useStore((state) => state.pageIntegrations);
  const generatePageInsights = useStore((state) => state.generatePageInsights);
  const subscribeToPageIntegrations = useStore(
    (state) => state.subscribeToPageIntegrations,
  );
  const { googleAnalyticsIntegration } = useDomainIntegrations(
    orgId,
    currentDomain?.id ?? null,
  );
  const isGaDomainConfigured = !!(
    googleAnalyticsIntegration.selectedAccountId &&
    googleAnalyticsIntegration.selectedPropertyId
  );
  const focusedItem = useStore((state) => state.focusedItem);
  const clearFocusedItem = useStore((state) => state.clearFocusedItem);
  const pendingWorkspaceSelection = useStore(
    (state) => state.pendingWorkspaceSelection,
  );
  const persistedCurrentDomainId = useStore(
    (state) => state.userData?.currentDomainId ?? null,
  );

  // The job, not the recommendation derived from it: the expanded card needs the
  // job's provenance stamp to link back to the conversation. One state keeps the
  // two from drifting across the setters below.
  const [selectedHeuristicJob, setSelectedHeuristicJob] =
    useState<HeuristicJob | null>(null);
  const [isExpandedCardOpen, setIsExpandedCardOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);
  const [isPageSheetOpen, setIsPageSheetOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [isIdeaSheetOpen, setIsIdeaSheetOpen] = useState(false);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<
    Set<UnifiedRowKind>
  >(() => new Set(ENTITY_TYPE_OPTIONS.map(({ value }) => value)));
  const [savingManualVersionPageId, setSavingManualVersionPageId] = useState<
    string | null
  >(null);
  const [optimisticPageEditLocks, setOptimisticPageEditLocks] = useState<
    Record<string, boolean>
  >({});
  const pageEditLockGuardsRef = useRef<Record<string, boolean>>({});
  const pageEditLockTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const activeSelectedIdea = selectedIdea
    ? (ideas.find((idea) => idea.id === selectedIdea.id) ?? selectedIdea)
    : null;
  const activeSelectedPage = selectedPage
    ? (pages.find((page) => page.id === selectedPage.id) ?? selectedPage)
    : null;
  const activeSelectedPageIdea =
    activeSelectedPage?.idea_id != null
      ? (ideas.find((idea) => idea.id === activeSelectedPage.idea_id) ?? null)
      : null;

  const effectiveDomainId =
    pendingWorkspaceSelection?.domainId ??
    persistedCurrentDomainId ??
    currentDomain?.id ??
    null;

  // Filter pages by current domain; show all when no domain selected
  const filteredPages = useMemo(
    () =>
      effectiveDomainId
        ? pages.filter((p) => p.domainId === effectiveDomainId)
        : pages,
    [pages, effectiveDomainId],
  );

  // Handle focusedItem from search bar and assistant action badges. Idea selections
  // can arrive before the new domain's idea query has hydrated, so keep the
  // store value until the target idea is actually available.
  useEffect(() => {
    if (!focusedItem) return;

    if (focusedItem.type === "page") {
      const page = pages.find((p) => p.id === focusedItem.id);
      if (!page) {
        return;
      }

      clearFocusedItem();
      queueMicrotask(() => {
        setSelectedPage(page);
        setIsPageSheetOpen(true);
      });
      return;
    }

    if (focusedItem.type === "idea") {
      const idea = ideas.find((i) => i.id === focusedItem.id);
      if (!idea) {
        return;
      }

      clearFocusedItem();
      queueMicrotask(() => {
        setSelectedIdea(idea);
        setIsIdeaSheetOpen(true);
      });
      return;
    }

    if (focusedItem.type === "audit_job") {
      const job = completedJobsWithRecommendations.find(
        (candidate) => candidate.id === focusedItem.id,
      );
      if (!job) {
        return;
      }

      const heuristicJob = job as HeuristicJob;
      const recommendation = heuristicJob.results?.ai_recommendation?.valid_json;
      if (!recommendation) {
        clearFocusedItem();
        return;
      }

      clearFocusedItem();
      queueMicrotask(() => {
        setSelectedHeuristicJob(heuristicJob);
        setIsExpandedCardOpen(true);
      });
      return;
    }

    // ga_insight currently has no dedicated expanded card; landing on the
    // audits tab is still useful, and clearing prevents stale focus state.
    clearFocusedItem();
  }, [
    focusedItem,
    clearFocusedItem,
    pages,
    ideas,
    completedJobsWithRecommendations,
  ]);

  // Use refs instead of state - these are only for control flow, not rendering
  const errorToastJobIdRef = useRef<string | null>(null);
  const isDismissingToastRef = useRef(false);

  useEffect(() => {
    // Skip showing toast if we're in the process of dismissing one
    if (isDismissingToastRef.current) {
      return;
    }

    if (
      recentErrorJob &&
      (!errorToastJobIdRef.current ||
        recentErrorJob.id !== errorToastJobIdRef.current)
    ) {
      toast({
        variant: "destructive",
        title: "Analysis failed",
        richColors: true,
        description: "An analysis has failed. Please try again.",
        persistent: true,
        closeButton: true,
        onDismiss: () => {
          isDismissingToastRef.current = true;
          errorToastJobIdRef.current = recentErrorJob.id;
          setTimeout(() => {
            isDismissingToastRef.current = false;
          }, 300);
        },
      });
      errorToastJobIdRef.current = recentErrorJob.id;
    } else if (!recentErrorJob) {
      if (!isDismissingToastRef.current) {
        errorToastJobIdRef.current = null;
      }
    }
  }, [recentErrorJob, toast]);

  useEffect(() => {
    if (!orgId || !currentDomain?.id) {
      return;
    }

    filteredPages
      .filter((page) => page.source === "wordpress")
      .forEach((page) => {
        subscribeToPageVersions(orgId, currentDomain.id, page.id);
      });
  }, [
    currentDomain?.id,
    orgId,
    filteredPages,
    subscribeToPageVersions,
  ]);

  const handleAuditRowClick = (job: Job) => {
    const heuristicJob = job as HeuristicJob;
    if (heuristicJob.results?.ai_recommendation?.valid_json) {
      setSelectedHeuristicJob(heuristicJob);
      setIsExpandedCardOpen(true);
    }
  };

  const handleCloseExpandedCard = () => {
    setIsExpandedCardOpen(false);
    setTimeout(() => {
      setSelectedHeuristicJob(null);
    }, 300);
  };

  const loadPageVersions = (page: Page) => {
    if (
      page.source !== "wordpress" ||
      !orgId ||
      !currentDomain?.id
    ) {
      return;
    }

    subscribeToPageVersions(orgId, currentDomain.id, page.id);
  };

  const handlePageRowClick = (page: Page) => {
    setSelectedPage(page);
    setIsPageSheetOpen(true);
    if (page.source === "wordpress") {
      loadPageVersions(page);
    }
  };

  const handleClosePageSheet = () => {
    setIsPageSheetOpen(false);
    setTimeout(() => {
      setSelectedPage(null);
    }, 300);
  };

  const handleRefreshGAInsights = async (page: Page) => {
    if (!orgId || !currentDomain?.id) return;
    try {
      await generatePageInsights(orgId, currentDomain.id, page.id);
    } catch (error) {
      console.error("Failed to refresh GA insights:", error);
    }
  };

  useEffect(() => {
    if (
      !isPageSheetOpen ||
      !selectedPage?.id ||
      !orgId ||
      !currentDomain?.id
    )
      return;
    const unsub = subscribeToPageIntegrations(
      orgId,
      currentDomain.id,
      selectedPage.id,
    );
    return () => {
      unsub();
    };
  }, [
    isPageSheetOpen,
    selectedPage?.id,
    orgId,
    currentDomain?.id,
    subscribeToPageIntegrations,
  ]);

  const gaForPage = selectedPage?.id
    ? pageIntegrations[selectedPage.id]?.googleAnalytics
    : undefined;
  const gaLatestInsightAt = useMemo(() => {
    const list = gaForPage?.insights ?? [];
    if (list.length === 0) return null;
    return list.reduce((max, i) => (i.created_at > max ? i.created_at : max), list[0].created_at);
  }, [gaForPage?.insights]);

  const handleIdeaRowClick = (idea: Idea) => {
    setSelectedIdea(idea);
    setIsIdeaSheetOpen(true);
  };

  const handleCloseIdeaSheet = () => {
    setIsIdeaSheetOpen(false);
    setTimeout(() => {
      setSelectedIdea(null);
    }, 300);
  };

  const handleSaveManualVersion = async (
    page: Page,
    baseVersion: WordPressVersion,
    edits: Pick<
      WordPressVersion,
      "wp_title" | "wp_slug" | "wp_excerpt" | "wp_content"
    > & { wp_categories?: number[] },
  ) => {
    if (!currentUser?.uid || !currentDomain?.id) {
      toast({
        variant: "destructive",
        title: "No domain selected",
        description: "Select a domain before editing a page draft.",
      });
      return;
    }

    setSavingManualVersionPageId(page.id);
    try {
      await saveManualPageVersion(
        currentDomain.id,
        page.id,
        baseVersion,
        edits,
      );
      toast({
        variant: "success",
        title: "Draft saved",
        description: "A new manual-edit version was created.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to save draft",
        description:
          error instanceof Error
            ? error.message
            : "The manual edit could not be saved.",
      });
      throw error;
    } finally {
      setSavingManualVersionPageId(null);
    }
  };

  const handleCreateFromIdea = async (
    idea: Idea,
    wpType: "posts" | "pages",
    customInstructions?: string,
  ) => {
    if (!currentUser?.uid || !currentDomain?.id) {
      toast({
        variant: "destructive",
        title: "No domain selected",
        description: "Select a domain before creating WordPress content.",
      });
      return;
    }

    markIdeaCreatePending(currentUser.uid, currentDomain.id, idea.id, {
      wpType,
    });

    try {
      const response =
        wpType === "posts"
          ? await WordPressService.createPostFromIdea(
              currentDomain.id,
              idea.id,
              customInstructions?.trim() || undefined,
            )
          : await WordPressService.createPageFromIdea(
              currentDomain.id,
              idea.id,
              customInstructions?.trim() || undefined,
            );

      markIdeaCreatePending(currentUser.uid, currentDomain.id, idea.id, {
        pageId: response.page_id ?? null,
        wpType,
      });
      toast({
        variant: "success",
        title: wpType === "posts" ? "Post draft started" : "Page draft started",
        description:
          "Draft generation is running in the background. This idea will update when implementation finishes.",
      });
    } catch (error) {
      if (error instanceof WordPressIdeaConflictError) {
        resolveIdeaAsImplemented(
          currentUser.uid,
          currentDomain.id,
          idea.id,
          error.implementedPageId,
          idea.implemented_page_type ?? null,
        );
        toast({
          variant: "default",
          title: "Idea already implemented",
          description:
            "This idea was already turned into a page. The idea state has been updated.",
        });
        return;
      }

      clearIdeaUiOverride(currentUser.uid, currentDomain.id, idea.id);

      toast({
        variant: "destructive",
        title: "Failed to start draft generation",
        description:
          error instanceof Error ? error.message : "The create request failed.",
      });
    }
  };

  const handleOpenLinkedPage = (idea: Idea) => {
    if (!idea.implemented_page_id) {
      toast({
        variant: "default",
        title: "Linked page unavailable",
        description:
          "This idea is implemented, but no linked page ID was saved.",
      });
      return;
    }

    const linkedPage = pages.find(
      (page) => page.id === idea.implemented_page_id,
    );
    if (!linkedPage) {
      toast({
        variant: "default",
        title: "Linked page not loaded",
        description: `Page ${idea.implemented_page_id} is not available in the current page list yet.`,
      });
      return;
    }

    setIsIdeaSheetOpen(false);
    setSelectedIdea(null);
    setSelectedPage(linkedPage);
    setIsPageSheetOpen(true);
    loadPageVersions(linkedPage);
  };

  const handleOpenLinkedIdea = (page: Page) => {
    if (!page.idea_id) {
      toast({
        variant: "default",
        title: "Linked idea unavailable",
        description: "This page does not have a source idea ID saved.",
      });
      return;
    }

    const linkedIdea = ideas.find((idea) => idea.id === page.idea_id);
    if (!linkedIdea) {
      toast({
        variant: "default",
        title: "Linked idea not loaded",
        description: `Idea ${page.idea_id} is not available in the current idea list yet.`,
      });
      return;
    }

    setIsPageSheetOpen(false);
    setSelectedPage(null);
    setSelectedIdea(linkedIdea);
    setIsIdeaSheetOpen(true);
  };

  const handleEditPage = async (page: Page, customInstructions?: string) => {
    if (!currentUser?.uid || !currentDomain?.id) {
      toast({
        variant: "destructive",
        title: "No domain selected",
        description: "Select a domain before editing WordPress content.",
      });
      return;
    }

    if (page.source !== "wordpress") {
      return;
    }

    if (pageEditLockGuardsRef.current[page.id] || wpActionLoading[page.id]) {
      return;
    }

    setPageEditLock(page.id);

    try {
      await editWpContent(
        currentDomain.id,
        page.id,
        page.wordpress?.wp_type === "page" ? "pages" : "posts",
        customInstructions?.trim() || undefined,
      );
      loadPageVersions(page);
    } catch (error) {
      clearPageEditLock(page.id);
      throw error;
    }
  };

  const handleRunPageAudit = async (page: Page) => {
    if (!page.url) {
      toast({
        variant: "destructive",
        title: "Missing page URL",
        description: "This page cannot be audited because it has no URL.",
      });
      return;
    }

    await startAnalysisJob(page.url);
  };

  const handleApprovePageVersion = async (
    page: Page,
    version: {
      id: string;
      wp_type?: string | null;
      review_status?: string | null;
    },
  ) => {
    if (!currentUser?.uid || !currentDomain?.id) {
      toast({
        variant: "destructive",
        title: "No domain selected",
        description: "Select a domain before approving WordPress content.",
      });
      return;
    }

    if (version.review_status !== "ready_for_review") {
      return;
    }

    await approveWpVersion(
      currentDomain.id,
      page.id,
      version.id,
      version.wp_type === "page" ? "pages" : "posts",
    );
  };

  const renderFooter = () => {
    const auditCount = completedJobsWithRecommendations.length;
    const pageCount = filteredPages.length;
    const ideaCount = ideas.length;
    const total = auditCount + pageCount + ideaCount;
    const parts = [
      auditCount > 0 && `${auditCount} audit${auditCount === 1 ? "" : "s"}`,
      pageCount > 0 && `${pageCount} page${pageCount === 1 ? "" : "s"}`,
      ideaCount > 0 && `${ideaCount} idea${ideaCount === 1 ? "" : "s"}`,
    ].filter(Boolean);

    return (
      <span>
        {total} item{total === 1 ? "" : "s"}
        {parts.length > 0 ? ` · ${parts.join(", ")}` : ""}
        {pendingJobs.length > 0 &&
          ` (${pendingJobs.length} audit job${pendingJobs.length === 1 ? "" : "s"} in progress)`}
      </span>
    );
  };

  const unifiedRows: UnifiedRow[] = useMemo(
    () => [
      ...completedJobsWithRecommendations.map((job) =>
        auditToRow(job as HeuristicJob),
      ),
      ...filteredPages.map((page) =>
        pageToRow(page, wpVersions, isLoadingWpVersions),
      ),
      ...ideas.map((idea) => ideaToRow(idea, ideaUiOverrides)),
    ],
    [
      completedJobsWithRecommendations,
      filteredPages,
      wpVersions,
      isLoadingWpVersions,
      ideas,
      ideaUiOverrides,
    ],
  );

  const visibleUnifiedRows = useMemo(
    () => unifiedRows.filter((row) => selectedEntityTypes.has(row.kind)),
    [selectedEntityTypes, unifiedRows],
  );

  const handleEntityTypeChange = (
    entityType: UnifiedRowKind,
    isSelected: boolean,
  ) => {
    setSelectedEntityTypes((currentTypes) => {
      const nextTypes = new Set(currentTypes);
      if (isSelected) {
        nextTypes.add(entityType);
      } else {
        nextTypes.delete(entityType);
      }
      return nextTypes;
    });
  };

  const handleUnifiedRowClick = (row: UnifiedRow) => {
    if (row.kind === "audit") {
      handleAuditRowClick(row.raw as Job);
    } else if (row.kind === "page") {
      handlePageRowClick(row.raw as Page);
    } else {
      handleIdeaRowClick(row.raw as Idea);
    }
  };

  const isHydratingPendingWorkspace =
    pendingWorkspaceSelection != null &&
    filteredPages.every((page) => page.id !== pendingWorkspaceSelection.pageId);

  const isUnifiedLoading =
    (isLoadingJobs &&
      completedJobsWithRecommendations.length === 0 &&
      pendingJobs.length === 0) ||
    (isLoadingPages && filteredPages.length === 0) ||
    isHydratingPendingWorkspace ||
    (isLoadingIdeas && ideas.length === 0);

  const clearPageEditLock = useCallback((pageId: string) => {
    const timer = pageEditLockTimersRef.current[pageId];
    if (timer) {
      clearTimeout(timer);
      delete pageEditLockTimersRef.current[pageId];
    }
    delete pageEditLockGuardsRef.current[pageId];
    setOptimisticPageEditLocks((currentLocks) => {
      if (!currentLocks[pageId]) {
        return currentLocks;
      }
      const nextLocks = { ...currentLocks };
      delete nextLocks[pageId];
      return nextLocks;
    });
  }, []);

  const setPageEditLock = useCallback(
    (pageId: string) => {
      const existingTimer = pageEditLockTimersRef.current[pageId];
      if (existingTimer) {
        clearTimeout(existingTimer);
      }
      pageEditLockGuardsRef.current[pageId] = true;
      setOptimisticPageEditLocks((currentLocks) => ({
        ...currentLocks,
        [pageId]: true,
      }));
      pageEditLockTimersRef.current[pageId] = setTimeout(() => {
        clearPageEditLock(pageId);
      }, PAGE_EDIT_LOCK_TIMEOUT_MS);
    },
    [clearPageEditLock],
  );

  useEffect(() => {
    return () => {
      Object.values(pageEditLockTimersRef.current).forEach((timer) => {
        clearTimeout(timer);
      });
      pageEditLockTimersRef.current = {};
      pageEditLockGuardsRef.current = {};
    };
  }, []);

  useEffect(() => {
    Object.keys(optimisticPageEditLocks).forEach((pageId) => {
      const page = pages.find((candidatePage) => candidatePage.id === pageId);
      if (
        page &&
        isDraftInProgress(
          page,
          wpVersions[pageId],
          !!isLoadingWpVersions[pageId],
        )
      ) {
        clearPageEditLock(pageId);
      }
    });
  }, [
    clearPageEditLock,
    isLoadingWpVersions,
    optimisticPageEditLocks,
    pages,
    wpVersions,
  ]);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-2 pb-1">
        <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <legend className="sr-only">Filter by entity type</legend>
          {ENTITY_TYPE_OPTIONS.map(({ value, label }) => {
            const checkboxId = `entity-type-${value}`;

            return (
              <div key={value} className="flex items-center gap-2">
                <Checkbox
                  id={checkboxId}
                  checked={selectedEntityTypes.has(value)}
                  onCheckedChange={(checked) =>
                    handleEntityTypeChange(value, checked === true)
                  }
                />
                <label
                  htmlFor={checkboxId}
                  className="cursor-pointer text-sm font-medium text-foreground"
                >
                  {label}
                </label>
              </div>
            );
          })}
        </fieldset>
        <ViewToggle
          viewMode={viewMode}
          onViewModeChange={(mode) => setViewMode(currentUser?.uid ?? null, mode)}
        />
      </div>

      {viewMode === "table" ? (
        <DataTable
          columns={unifiedColumns}
          data={visibleUnifiedRows}
          getRowId={(row) => `${row.kind}-${row.id}`}
          onRowClick={handleUnifiedRowClick}
          is_loading={isUnifiedLoading}
          emptyMessage={
            selectedEntityTypes.size === 0
              ? "Select at least one entity type to show results."
              : "No matching analyses, pages, or ideas yet."
          }
        />
      ) : (
        <div className="px-4">
          <EntityCardGrid
            rows={visibleUnifiedRows}
            onRowClick={handleUnifiedRowClick}
            is_loading={isUnifiedLoading}
            emptyMessage={
              selectedEntityTypes.size === 0
                ? "Select at least one entity type to show results."
                : "No matching analyses, pages, or ideas yet."
            }
          />
        </div>
      )}

      {selectedHeuristicJob && (
        <CROExpandedCard
          isOpen={isExpandedCardOpen}
          onClose={handleCloseExpandedCard}
          job={selectedHeuristicJob}
        />
      )}

      {activeSelectedPage && (
        <PageExpandedSheet
          page={activeSelectedPage}
          isOpen={isPageSheetOpen}
          onClose={handleClosePageSheet}
          versions={wpVersions[activeSelectedPage.id] ?? []}
          isLoadingVersions={!!isLoadingWpVersions[activeSelectedPage.id]}
          versionError={wpVersionErrors[activeSelectedPage.id] ?? null}
          onLoadVersions={loadPageVersions}
          onEditPage={canEdit ? handleEditPage : undefined}
          onApproveVersion={canEdit ? handleApprovePageVersion : undefined}
          onRunAudit={canEdit ? handleRunPageAudit : undefined}
          linkedIdea={activeSelectedPageIdea}
          onOpenLinkedIdea={handleOpenLinkedIdea}
          isEditingPage={
            !!wpActionLoading[activeSelectedPage.id] ||
            !!optimisticPageEditLocks[activeSelectedPage.id]
          }
          versionActionLoading={wpActionLoading}
          isRunningAudit={isAnalyzing || isGeneratingAnalysis}
          onRefreshGAInsights={canEdit ? handleRefreshGAInsights : undefined}
          isGaDomainConfigured={isGaDomainConfigured}
          isRefreshingGaInsights={!!gaForPage?.isGeneratingInsights}
          gaGenerationJob={gaForPage?.generationJob ?? null}
          gaLatestInsightAt={gaLatestInsightAt}
          onSaveManualVersion={canEdit ? handleSaveManualVersion : undefined}
          isSavingManualVersion={
            savingManualVersionPageId === activeSelectedPage.id
          }
          availableCategories={wpCategories}
        />
      )}

      {activeSelectedIdea && (
        <IdeaExpandedSheet
          idea={activeSelectedIdea}
          ideaUiOverride={ideaUiOverrides[activeSelectedIdea.id]}
          isOpen={isIdeaSheetOpen}
          onClose={handleCloseIdeaSheet}
          onCreateDraft={canEdit ? handleCreateFromIdea : undefined}
          onOpenLinkedPage={handleOpenLinkedPage}
          isWordPressConnected={isWordPressConnected(wpIntegration)}
        />
      )}

      <div className="text-sm text-gray-500 mt-4 px-4">{renderFooter()}</div>
    </>
  );
};
